// WHAT: Oura-Dashboard-Organism, der sämtliche Oura-Metrik-Karten (Activity, Readiness, Sleep, SpO2, Stress, HRV, Workouts, Long-Term) aggregiert
// WHY:  Bietet einen konsolidierten Gesundheitsüberblick auf Basis der Oura-Daten im Health-Workspace
// DOMAIN: health → oura → organisms
// DEPENDENCIES: Optimierte Oura-Hooks (`useOptimized*`), diverse Molecules/Organisms (StepsCard, ActivityGoal, VascularAgeCard etc.), Logging-Utils
// RISKS:
//   - NOTE: Kombiniert viele unabhängige Hooks; Fehler oder Latenzen in einem Hook können das gesamte Dashboard blockieren
//   - NOTE: Harte Zielwerte (z.B. 10.000 Schritte) sind aktuell fix kodiert und sollten bei Bedarf zentral konfiguriert werden
import { type FC } from "react";

import {
  useOptimizedActivity,
  useOptimizedFullSleep,
  useOptimizedHeartRate,
  useOptimizedReadiness,
  useOptimizedSleep,
  useOptimizedSpO2,
  useOptimizedStress,
  useOptimizedWorkouts,
} from "../../hooks";
import { ActivityGoal } from "../molecules/ActivityGoal";
import { ActivityScore } from "../molecules/ActivityScore";
import { HRVCard } from "../molecules/HRVCard";
import { ReadinessScore } from "../molecules/ReadinessScore";
import { SleepScore } from "../molecules/SleepScore";
import { SpO2Card } from "../molecules/SpO2Card";
import { StepsCard } from "../molecules/StepsCard";
import { StressCard } from "../molecules/StressCard";
import { VascularAgeCard } from "../molecules/VascularAge";
import { WorkoutSummaryCard } from "../molecules/WorkoutSummaryCard";

/**
 * WHAT:
 *   Organism, der alle relevanten Oura-Metriken (Activity, Readiness, Sleep, SpO2, Stress,
 *   HRV, Workouts und Long-Term-Metriken wie ActivityGoal/VascularAge) in einer Dashboard-Ansicht bündelt.
 *
 * WHY:
 *   Dient als zentrale Übersichtsseite für Oura-Daten und fasst Informationen zusammen,
 *   die sonst auf mehrere Detailseiten verteilt wären.
 *
 * BEHAVIOR:
 *   - Lädt Daten über mehrere `useOptimized*`-Hooks und kombiniert deren Lade-/Fehlerzustände.
 *   - Zeigt bei globalem Laden/Fehler einen einfachen Status und erst bei Erfolg die vollständige Grid-Struktur.
 *
 * RISKS:
 *   - NOTE: Rückgabewerte der Hooks müssen stabil typisiert sein; Änderungen an den Optimized-Hooks können das Layout beeinflussen.
 *
 * @returns Oura dashboard component
 *
 */
export const OuraDashboard: FC = () => {
  const {
    activity,
    error: errorActivity,
    loading: loadingActivity,
    score: activityScore,
    steps,
  } = useOptimizedActivity();

  // Berechne Activity Goal Daten
  const activeCalories = activity?.active_calories ?? 0;
  const baseTarget = activity?.target_calories ?? 2000;
  const sedentaryTime = activity?.sedentary_time ?? 0;

  const {
    error: errorReadiness,
    loading: loadingReadiness,
    score: readinessScore,
  } = useOptimizedReadiness();

  const {
    error: errorSleep,
    loading: loadingSleep,
    score: sleepScore,
    sleep,
  } = useOptimizedSleep();

  const { error: errorSpO2, loading: loadingSpO2, spo2 } = useOptimizedSpO2();

  const { error: errorStress, loading: loadingStress, stress } = useOptimizedStress();

  const workoutsData = useOptimizedWorkouts();
  const { error: errorWorkouts, loading: loadingWorkouts } = workoutsData;

  const { error: errorHeartRate, heartrate, loading: loadingHeartRate } = useOptimizedHeartRate();

  const {
    averageHeartRate,
    averageHRV,
    error: errorFullSleep,
    loading: loadingFullSleep,
    maxHRV,
  } = useOptimizedFullSleep();

  // Lade- und Fehlerzustände kombinieren
  const isLoading =
    loadingActivity ||
    loadingReadiness ||
    loadingSleep ||
    loadingSpO2 ||
    loadingStress ||
    loadingWorkouts ||
    loadingHeartRate ||
    loadingFullSleep;
  const hasError =
    errorActivity ??
    errorReadiness ??
    errorSleep ??
    errorSpO2 ??
    errorStress ??
    errorWorkouts ??
    errorHeartRate ??
    errorFullSleep;
  const errorMessage =
    errorActivity ??
    errorReadiness ??
    errorSleep ??
    errorSpO2 ??
    errorStress ??
    errorWorkouts ??
    errorHeartRate ??
    errorFullSleep;

  if (isLoading) {
    return <div className="text-sm text-[var(--textMuted)]">Loading...</div>;
  }

  if (hasError) {
    return <div className="text-sm text-[var(--errorRed)]">Error: {errorMessage}</div>;
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Core Metrics */}
      <section>
        <h2 className="text-[var(--textMuted)] text-xs uppercase tracking-widest pl-1 mb-6">
          Core Metrics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StepsCard
            goal={10_000} // 🎯 Fix auf 10.000 Schritte
            steps={steps}
            {...(activity && "streakCount" in activity && activity.streakCount !== undefined
              ? { streakCount: activity.streakCount as number }
              : {})}
          />
          <ActivityScore score={activityScore} />
          <ReadinessScore score={readinessScore} />
          <SleepScore score={sleepScore} />
        </div>
      </section>

      {/* Health Metrics */}
      <section>
        <h2 className="text-[var(--textMuted)] text-xs uppercase tracking-widest pl-1 mb-6">
          Health Metrics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <SpO2Card
            {...(spo2?.average !== undefined && { average: spo2.average })}
            {...(spo2?.breathing_disturbance_index !== undefined && {
              breathingDisturbanceIndex: spo2.breathing_disturbance_index,
            })}
          />
          <StressCard
            {...(stress?.recovery_high !== undefined && { recoveryHigh: stress.recovery_high })}
            {...(stress?.stress_high !== undefined && { stressHigh: stress.stress_high })}
          />
          <HRVCard
            {...(heartrate?.average_awake_bpm !== undefined && {
              averageAwakeBpm: heartrate.average_awake_bpm,
            })}
            {...(averageHRV !== undefined && { averageHRV })}
            {...(averageHeartRate !== undefined && { averageNightHeartRate: averageHeartRate })}
            {...(heartrate?.average_rest_bpm !== undefined && {
              averageRestBpm: heartrate.average_rest_bpm,
            })}
            {...(maxHRV !== undefined && { maxHRV })}
            {...(sleep?.resting_heart_rate !== undefined && {
              restingHeartRate: sleep.resting_heart_rate,
            })}
          />
          <WorkoutSummaryCard
            intensityMinutes={workoutsData.intensityMinutes}
            totalCalories={workoutsData.totalCalories}
            workouts={workoutsData.workouts}
          />
        </div>

        {/* Long-Term Metrics Section */}
        <section>
          <h2 className="text-[var(--textMuted)] text-xs uppercase tracking-widest pl-1 mb-6">
            Long-Term Metrics
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="space-y-4">
              <ActivityGoal
                activeCalories={activeCalories}
                activityScore={activityScore}
                baseTarget={baseTarget}
                sedentaryTime={sedentaryTime}
              />
            </div>
            <div className="space-y-4">
              <VascularAgeCard />
            </div>
          </div>
        </section>
      </section>
    </div>
  );
};
